import {getDomainGraphqlError, toRelayId} from 'graphqlsupport';
import {ReturnedDeclinedPartyOptions, ReturnedReasonOptions, UnmatchedReasonOptions} from 'placement/const';
import {useAsyncState} from 'placement/hook/useAsyncState';
import {useReturnOffer} from 'placement/hook/useReturnOffer';
import {Offer, Referral} from 'placement/domains';
import React from 'react';
import {useUnmatchOffer} from "../hook/useUnmatchOffer";
import {useGoToReferralListPage} from "../routes";
import { showToast } from 'common';

type ProviderOfferReturnedModalProps = {
    modalState: UnmatchReturnOfferModalState;
    setHideModal: () => void;
    referral: Referral;
    selectedOffer: Offer | null;
}

export type UnmatchReturnOfferModalState = 'Unmatch' | 'Return' | 'hide';


export function ProviderOfferReturnedModal({
                                               modalState, selectedOffer, setHideModal, referral
                                           }: ProviderOfferReturnedModalProps) {
    const [loading, setLoading] = useAsyncState<boolean>(false);
    const modalRef = React.useRef(null);
    const [declinedParty, setDeclinedParty] = React.useState('')
    const [reason, setReason] = React.useState('')

    const disabledConfirm = reason === '' || declinedParty === '';
    let disabledCss = '';
    if (disabledConfirm) {
        disabledCss = 'opacity-50 cursor-not-allowed';
    }

    const cancel = () => {
        setDeclinedParty('');
        setReason('');
        hideModal();
    }

    let loadingTextCss = '';
    let saveLabel = 'Save';
    let cancelBtnDisabledCss = '';
    if (loading) {
        loadingTextCss = 'loading-text';
        saveLabel = 'Saving';
        cancelBtnDisabledCss = 'opacity-50 cursor-not-allowed';
        disabledCss = 'opacity-50 cursor-not-allowed';
    }

    const hideModal = React.useCallback(() => {
        if (!loading) {
            setHideModal();
        }
    }, [loading, setHideModal])

    const goToReferralListPage = useGoToReferralListPage();

    const backToReferralList = React.useCallback(
        () => {
            goToReferralListPage();
        },
        [goToReferralListPage]
    );

    React.useEffect(() => {
        // close modal on outside modal click
        const handleClickOutside = (e: Event) => {
            if (modalRef && (modalRef.current as any).contains(e.target)) {
                // outside
                return;
            }
            hideModal();
        };
        if (modalState !== 'hide') {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [modalState, hideModal]);

    let offerId = ''
    if (selectedOffer) {
        offerId = toRelayId(selectedOffer.guid, 'Offer')
    }
    const {returnOffer} = useReturnOffer(offerId);
    const {unmatchOffer} = useUnmatchOffer(offerId);

    const submitReturnOffer = async () => {
        setLoading(true);
        let haveError = false;
        try {
            await returnOffer({declinedParty: declinedParty, reason: reason});
        } catch (e) {
            console.log(e);
            haveError = true;
            let errMessage = getDomainGraphqlError(e)
            if (!errMessage) {
                errMessage = 'Something went wrong when trying to return referral';
            }
            showToast.error(errMessage);
        }
        if (!haveError) {
            showToast.success(`Returned ${referral.caseNumber} success`);
        }
        setLoading(false);
        hideModal();
        backToReferralList();
    }

    const submitUnmatchOffer = async () => {
        setLoading(true);
        let haveError = false;
        try {
            await unmatchOffer({declinedParty: declinedParty, reason: reason});
        } catch (e) {
            console.log(e);
            haveError = true;
            let errMessage = getDomainGraphqlError(e)
            if (!errMessage) {
                errMessage = 'Something went wrong when trying to unmatch referral';
            }
            showToast.error(errMessage);
        }
        if (!haveError) {
            showToast.success(`Unmatch ${referral.caseNumber} success`);
        }
        setLoading(false);
        hideModal();
        backToReferralList();
    }

    const declinedPartyPlaceholderClass = declinedParty === '' ? 'text-gray-400' : '';
    const reasonPlaceholderClass = reason === '' ? 'text-gray-400' : ''

    let options = ReturnedReasonOptions;
    if (modalState === 'Unmatch') {
        options = UnmatchedReasonOptions;
    }

    return (
        <React.Fragment>
            {
                modalState !== 'hide' ? (
                    <>
                        <div
                            className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
                            <div className='relative w-auto my-6 mx-auto max-w-3xl'>
                                <div ref={modalRef}
                                     className='border-0 rounded shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                                    <div className='flex items-start justify-between p-5 rounded-t'>
                                        <h3 className='text-base font-semibold'>
                                            {`${modalState === 'Return' ? 'Return' : 'Unmatch'} Offer (${referral.caseNumber})`}
                                        </h3>
                                    </div>
                                    <div className='relative p-6 flex-auto'>
                                        <div className='inline-block relative w-full'>
                                            <select
                                                data-testid="provider-offer-returned-modal-party"
                                                value={declinedParty}
                                                className={`block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline ${declinedPartyPlaceholderClass}`}
                                                onChange={(e) => setDeclinedParty(e.target.value)}>
                                                {
                                                    ReturnedDeclinedPartyOptions.map((option) => {
                                                        return <option disabled={option.value === ''}
                                                                       className={option.value === '' ? 'text-gray-500' : 'text-black'}
                                                                       key={option.value}
                                                                       value={option.value}>{option.label}</option>
                                                    })
                                                }
                                            </select>
                                            <div
                                                className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                                <svg className="fill-current h-4 w-4"
                                                     xmlns="https://www.w3.org/2000/svg"
                                                     viewBox="0 0 20 20">
                                                    <path
                                                        d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                                                </svg>
                                            </div>
                                        </div>
                                        <div className='mt-5 inline-block relative w-full'>
                                            <select
                                                data-testid="provider-offer-returned-modal-reason"
                                                value={reason}
                                                className={`block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline ${reasonPlaceholderClass}`}
                                                onChange={(e) => setReason(e.target.value)}>
                                                {
                                                    options.map((option) => {
                                                        return <option disabled={option.value === ''}
                                                                       key={option.value}
                                                                       className={option.value === '' ? 'text-gray-500' : 'text-black'}
                                                                       value={option.value}>{option.label}</option>
                                                    })
                                                }
                                            </select>
                                            <div
                                                className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                                <svg className="fill-current h-4 w-4"
                                                     xmlns="https://www.w3.org/2000/svg"
                                                     viewBox="0 0 20 20">
                                                    <path
                                                        d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex items-center justify-end p-6 pt-16 rounded-b'>
                                        <button
                                            className={`text-atlantis-200 background-transparent px-10 py-3 text-sm outline-none focus:outline-none
                                                        mr-4 mb-1 border border-atlantis-200 rounded ${cancelBtnDisabledCss}`}
                                            type='button'
                                            disabled={loading}
                                            onClick={cancel}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={`bg-atlantis-200 text-white active:bg-atlantis-300 font-semibold text-sm px-10 py-3 shadow hover:shadow-lg
                                                    outline-none focus:outline-none mr-1 mb-1 rounded ${loadingTextCss} ${disabledCss}`}
                                            type='button'
                                            disabled={declinedParty === '' || reason === '' || loading}
                                            style={{transition: 'all .15s ease'}}
                                            onClick={modalState === 'Return' ? submitReturnOffer : submitUnmatchOffer}
                                        >
                                            {saveLabel}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
                    </>
                ) : null
            }
        </React.Fragment>
    );
}
